import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getSheetCols, SHEET_DEPT_META } from '../../config/constants';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, STATUS_STYLE, btn, inp } from '../../config/styles';
import { makeProduct, autoAdvanceStatus, calcOverallScore } from '../../utils/products';
import { sanitizeHtml } from '../../utils/sanitize';

function LongTextModal({ title, value, isHtml, onSave, onClose }) {
  const [val, setVal] = useState(value || '');
  const [preview, setPreview] = useState(false);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:660, maxHeight:'82vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div style={{ fontFamily:F.display, fontSize:18, color:C.text }}>{title}</div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {isHtml && (
              <button style={btn('ghost', { fontSize:12, padding:'4px 10px', border:`1px solid ${C.e8}`, borderRadius:6 })} onClick={() => setPreview(v => !v)}>
                {preview ? 'Edit' : 'Preview'}
              </button>
            )}
            <button style={btn('ghost', { fontSize:20, padding:'2px 8px', color:C.muted })} onClick={onClose}>×</button>
          </div>
        </div>
        <div style={{ flex:1, overflow:'auto', marginBottom:16 }}>
          {preview
            ? <div style={{ border:`1px solid ${C.e8}`, borderRadius:6, padding:16, minHeight:320, fontSize:14, lineHeight:1.8, color:C.text }} dangerouslySetInnerHTML={{ __html: val ? sanitizeHtml(val) : '<em style="color:#aaa">No content yet</em>' }} />
            : <textarea autoFocus style={{ width:'100%', height:320, ...inp(), fontFamily:F.mono, fontSize:12, resize:'vertical', lineHeight:1.6 }} value={val} onChange={e => setVal(e.target.value)} />
          }
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={btn('secondary')} onClick={onClose}>Cancel</button>
          <button style={btn('primary')} onClick={() => { onSave(val); onClose(); }}>Save</button>
        </div>
      </div>
    </div>
  );
}

export function SpreadsheetView({ collection, roles, onUpdateProducts }) {
  const tenantConfig = useTenantConfig();
  const { deptIds, deptLabels } = tenantConfig;
  const cols = useMemo(() => getSheetCols(tenantConfig), [tenantConfig]);
  const products  = collection?.products || [];
  const [editing,  setEditing]  = useState(null);  // { rowIdx, colKey }
  const [editVal,  setEditVal]  = useState('');
  const [modal,    setModal]    = useState(null);  // { rowIdx, col }
  const [selected, setSelected] = useState(null);  // { rowIdx, colKey } — focused cell
  const [clipboard,setClipboard]= useState(null);  // string value last copied
  const [fillDrag, setFillDrag] = useState(null);  // { colKey, value, startRow, endRow }
  const [hovRow,   setHovRow]   = useState(null);
  const inputRef   = useRef(null);
  const isManager  = roles?.includes('manager') ?? false;

  const canEdit    = col => isManager || roles?.includes(col.dept);
  const getCellVal = (p, col) => col.key === 'tags' ? (p.tags || []).join(', ') : (p[col.key] || '');
  const applyVal   = (p, col, val) => {
    let base;
    if (col.key === 'tags') {
      base = { ...p, tags: val.split(',').map(s => s.trim()).filter(Boolean) };
    } else if (col.key === 'hsCode' || col.key === 'coo') {
      base = { ...p, [col.key]: val, variants: (p.variants || []).map(v => ({ ...v, [col.key]: val })) };
    } else {
      base = { ...p, [col.key]: val };
    }
    return autoAdvanceStatus({ ...base, updatedAt: new Date().toISOString() }, tenantConfig);
  };

  const commitEdit = (rowIdx, col, val) => {
    onUpdateProducts(products.map((p, i) => i === rowIdx ? applyVal(p, col, val) : p));
    setEditing(null);
  };

  const startEdit = (rowIdx, col) => {
    if (!canEdit(col)) return;
    const val = getCellVal(products[rowIdx], col);
    setSelected({ rowIdx, colKey: col.key });
    if (col.type === 'longtext') { setModal({ rowIdx, col, value: val }); return; }
    setEditing({ rowIdx, colKey: col.key });
    setEditVal(val);
  };

  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const handleKeyDown = (e, rowIdx, col, colIdx) => {
    if (e.key === 'Escape') { setEditing(null); return; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      commitEdit(rowIdx, col, editVal);
      const dir = e.shiftKey ? -1 : 1;
      let next = colIdx + dir;
      while (next >= 0 && next < cols.length) {
        const nc = cols[next];
        if (canEdit(nc) && nc.type !== 'longtext') { setTimeout(() => startEdit(rowIdx, nc), 0); break; }
        next += dir;
      }
    }
  };

  // Ctrl/Cmd + C/V on selected cell (skip when a text input is active)
  useEffect(() => {
    const handler = e => {
      if (editing || !selected) return;
      const col = cols.find(c => c.key === selected.colKey);
      if (!col) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const val = getCellVal(products[selected.rowIdx], col);
        setClipboard(val);
        navigator.clipboard?.writeText(val).catch(() => {});
        e.preventDefault();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard !== null && canEdit(col)) {
        onUpdateProducts(products.map((p, i) => i === selected.rowIdx ? applyVal(p, col, clipboard) : p));
        e.preventDefault();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && canEdit(col) && col.type !== 'longtext') {
        onUpdateProducts(products.map((p, i) => i === selected.rowIdx ? applyVal(p, col, '') : p));
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, selected, clipboard, products]);

  // Fill-drag: apply value to all rows in range on mouse-up
  useEffect(() => {
    if (!fillDrag) return;
    const onUp = () => {
      const col = cols.find(c => c.key === fillDrag.colKey);
      if (col && fillDrag.endRow !== fillDrag.startRow) {
        const lo = Math.min(fillDrag.startRow, fillDrag.endRow);
        const hi = Math.max(fillDrag.startRow, fillDrag.endRow);
        onUpdateProducts(products.map((p, i) => (i >= lo && i <= hi && canEdit(col)) ? applyVal(p, col, fillDrag.value) : p));
      }
      setFillDrag(null);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillDrag, products]);

  const deleteRow = rowIdx => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    onUpdateProducts(products.filter((_, i) => i !== rowIdx));
    if (selected?.rowIdx === rowIdx) setSelected(null);
    if (editing?.rowIdx  === rowIdx) setEditing(null);
  };

  const deptGroups = cols.reduce((acc, col) => {
    const last = acc[acc.length - 1];
    if (last && last.dept === col.dept) { last.count++; last.width += col.width; }
    else acc.push({ dept: col.dept, count: 1, width: col.width });
    return acc;
  }, []);

  const ROW_H = 36;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 94px)' }}>
      {/* Toolbar */}
      <div style={{ padding:'8px 20px', borderBottom:`1px solid ${C.e8}`, display:'flex', alignItems:'center', gap:10, background:C.fa, flexShrink:0 }}>
        <span style={{ fontSize:12, color:C.muted }}>{products.length} product{products.length !== 1 ? 's' : ''}</span>
        {clipboard !== null && (
          <span style={{ fontSize:11, color:C.muted, fontFamily:F.mono, background:C.f4, padding:'2px 8px', borderRadius:4 }}>
            Copied: "{clipboard.length > 40 ? clipboard.slice(0, 40) + '…' : clipboard}"
          </span>
        )}
        <div style={{ marginLeft:'auto' }}>
          {(isManager || roles?.includes('ops')) && (
            <button style={btn('primary')} onClick={() => onUpdateProducts([...products, makeProduct({}, tenantConfig)])}>+ Add product</button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex:1, overflow:'auto' }} onMouseLeave={() => { if (!fillDrag) setHovRow(null); }}>
        <table style={{ borderCollapse:'collapse', tableLayout:'fixed', width:'max-content', fontSize:12, userSelect: fillDrag ? 'none' : 'auto' }}>
          <thead style={{ position:'sticky', top:0, zIndex:20 }}>
            <tr>
              <th style={{ position:'sticky', left:0, zIndex:30, width:80, minWidth:80, background:C.fa, borderBottom:`1px solid ${C.e8}`, borderRight:`1px solid ${C.e8}` }} />
              {deptGroups.map(g => {
                const m = SHEET_DEPT_META[g.dept] || { label:'', hdr:C.e8, fg:C.text };
                return <th key={g.dept} colSpan={g.count} style={{ background:m.hdr, color:m.fg, fontFamily:F.body, fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', textAlign:'left', padding:'5px 10px', width:g.width, borderBottom:`1px solid ${C.e8}`, borderRight:`1px solid ${C.e8}`, whiteSpace:'nowrap' }}>{deptLabels[g.dept] || m.label}</th>;
              })}
            </tr>
            <tr>
              <th style={{ position:'sticky', left:0, zIndex:30, width:80, background:C.fa, borderBottom:`2px solid ${C.d0}`, borderRight:`1px solid ${C.e8}`, padding:'0 8px', fontFamily:F.body, fontSize:11, fontWeight:500, color:C.muted, textAlign:'left', height:32 }}>Status</th>
              {cols.map(col => {
                const m = SHEET_DEPT_META[col.dept] || { bg:C.fa };
                return <th key={col.key} style={{ width:col.width, minWidth:col.width, background:m.bg, borderBottom:`2px solid ${C.d0}`, borderRight:`1px solid ${C.e8}`, padding:'0 10px', fontFamily:F.body, fontSize:11, fontWeight:500, color:C.text2, textAlign:'left', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', height:32 }}>{col.label}</th>;
              })}
            </tr>
          </thead>

          <tbody>
            {products.length === 0 ? (
              <tr><td colSpan={cols.length + 1} style={{ padding:48, textAlign:'center', color:C.muted, fontSize:14 }}>No products yet. Click "+ Add product" to get started.</td></tr>
            ) : products.map((p, rowIdx) => {
              const score      = calcOverallScore(p, tenantConfig);
              const isHovering = hovRow === rowIdx;
              return (
                <tr key={p.id} style={{ height:ROW_H }}
                  onMouseEnter={() => { setHovRow(rowIdx); if (fillDrag) setFillDrag(prev => ({ ...prev, endRow: rowIdx })); }}
                >
                  {/* Status / delete cell */}
                  <td style={{ position:'sticky', left:0, zIndex:10, background:C.white, borderBottom:`1px solid ${C.f4}`, borderRight:`1px solid ${C.e8}`, padding:'0 6px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:4 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <div style={{ display:'flex', gap:2 }}>
                          {deptIds.map(d => { const s = STATUS_STYLE[p.sectionStatus[d] || 'empty']; return <div key={d} title={`${deptLabels[d]}: ${s.label}`} style={{ width:6, height:6, borderRadius:'50%', background:s.fg }} />; })}
                        </div>
                        <span style={{ fontSize:10, color:C.muted, fontFamily:F.mono }}>{score}%</span>
                      </div>
                      {(isManager || roles?.includes('ops')) && (
                        <button onClick={() => deleteRow(rowIdx)} title="Delete product" style={{ background:'none', border:'none', cursor:'pointer', color:'#dc2626', fontSize:15, lineHeight:1, padding:'0 2px', flexShrink:0, visibility: isHovering ? 'visible' : 'hidden' }}>×</button>
                      )}
                    </div>
                  </td>

                  {cols.map((col, colIdx) => {
                    const isEdit  = editing?.rowIdx  === rowIdx && editing?.colKey  === col.key;
                    const isSel   = selected?.rowIdx === rowIdx && selected?.colKey === col.key;
                    const inFill  = fillDrag?.colKey === col.key && rowIdx >= Math.min(fillDrag.startRow, fillDrag.endRow) && rowIdx <= Math.max(fillDrag.startRow, fillDrag.endRow);
                    const editable = canEdit(col);
                    const cellVal  = getCellVal(p, col);
                    const m        = SHEET_DEPT_META[col.dept] || { bg:'#fff' };

                    let display;
                    if (col.type === 'longtext') {
                      display = <span style={{ color: cellVal ? C.text2 : C.ph, fontStyle: cellVal ? 'normal' : 'italic', fontSize:11 }}>{cellVal ? `${cellVal.length} chars` : '-'}{editable && <span style={{ color:C.d0, marginLeft:4 }}>✎</span>}</span>;
                    } else if (col.key === 'tags') {
                      display = cellVal
                        ? <div style={{ display:'flex', gap:3, overflow:'hidden' }}>{(p.tags||[]).slice(0,3).map(t=><span key={t} style={{ fontSize:10, background:C.f4, padding:'1px 5px', borderRadius:3, whiteSpace:'nowrap', color:C.text2 }}>{t}</span>)}{p.tags.length>3&&<span style={{ fontSize:10, color:C.muted }}>+{p.tags.length-3}</span>}</div>
                        : <span style={{ color:C.ph, fontStyle:'italic' }}>-</span>;
                    } else {
                      display = <span style={{ color: cellVal ? C.text : C.ph, fontStyle: cellVal ? 'normal' : 'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' }}>{cellVal || '-'}</span>;
                    }

                    return (
                      <td key={col.key}
                        onClick={() => editable && startEdit(rowIdx, col)}
                        style={{ width:col.width, minWidth:col.width, background: inFill ? '#fef3c7' : isEdit||isSel ? '#fffdf0' : m.bg, borderBottom:`1px solid ${C.f4}`, borderRight:`1px solid ${C.e8}`, padding:0, cursor: editable ? (col.type==='longtext' ? 'pointer' : 'text') : 'default', outline: isEdit||isSel ? `2px solid #f59e0b` : 'none', outlineOffset:-2, position:'relative' }}
                      >
                        {isEdit
                          ? <input ref={inputRef} type={col.type === 'date' ? 'date' : 'text'} value={editVal} onChange={e => setEditVal(e.target.value)} onBlur={() => commitEdit(rowIdx, col, editVal)} onKeyDown={e => handleKeyDown(e, rowIdx, col, colIdx)} style={{ width:'100%', height:ROW_H, border:'none', background:'transparent', fontFamily:F.body, fontSize:12, color:C.text, padding:'0 10px', outline:'none' }} />
                          : <div style={{ padding:'0 10px', height:ROW_H, display:'flex', alignItems:'center', overflow:'hidden', position:'relative' }}>
                              {display}
                              {/* Fill handle — amber square bottom-right of selected cell */}
                              {isSel && editable && col.type !== 'longtext' && (
                                <div onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setFillDrag({ colKey: col.key, value: cellVal, startRow: rowIdx, endRow: rowIdx }); }} style={{ position:'absolute', bottom:0, right:0, width:8, height:8, background:'#f59e0b', cursor:'crosshair', zIndex:5 }} />
                              )}
                            </div>
                        }
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {(isManager || roles?.includes('ops')) && products.length > 0 && (
              <tr>
                <td colSpan={cols.length + 1} style={{ padding:'6px 12px', borderTop:`1px solid ${C.e8}` }}>
                  <button style={btn('ghost', { fontSize:12, color:C.muted, border:`1px dashed ${C.d0}`, width:'100%', justifyContent:'center', padding:'7px' })} onClick={() => onUpdateProducts([...products, makeProduct({}, tenantConfig)])}>+ Add product</button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <LongTextModal
          title={cols.find(c => c.key === modal.col.key)?.label}
          value={modal.value}
          isHtml={modal.col.key === 'bodyHtml'}
          onSave={val => onUpdateProducts(products.map((p, i) => i === modal.rowIdx ? applyVal(p, modal.col, val) : p))}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
